import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, Menu, X, Search, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useApp } from '../context/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Header = () => {
  const { user, cart, logout } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/categories`)
      .then((res) => setCategories(res.data))
      .catch(() => setCategories([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Main Header - Dark Shopify Style */}
      <div className="bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            
            {/* Left: Logo + Nav */}
            <div className="flex items-center gap-6 lg:gap-8">
              {/* Mobile Menu Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden h-10 w-10 text-white hover:bg-white/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>

              {/* Logo */}
              <Link to="/" className="flex-shrink-0" data-testid="header-logo">
                <div className="bg-white rounded-lg px-3 py-1.5">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_cozy-sleepwear-8/artifacts/lmyiwfsg_logo-vshoppe.jpeg" 
                    alt="VShoppe" 
                    className="h-10 lg:h-12 w-auto"
                  />
                </div>
              </Link>

              {/* Desktop Nav - Dynamic Categories */}
              <nav className="hidden lg:flex items-center gap-8">
                <Link 
                  to="/shop" 
                  className="text-base font-medium text-gray-300 hover:text-white transition-colors"
                  data-testid="nav-shop"
                >
                  Shop All
                </Link>
                {categories.slice(0, 5).map((cat) => (
                  <Link 
                    key={cat.id}
                    to={`/shop?category=${encodeURIComponent(cat.name)}`}
                    className="text-base font-medium text-gray-300 hover:text-white transition-colors whitespace-nowrap"
                    data-testid={`nav-category-${cat.name}`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Search */}
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-32 sm:w-48 h-9 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    autoFocus
                    data-testid="search-input"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="h-9 w-9 text-white hover:bg-white/10">
                    <X className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="h-9 w-9 text-gray-300 hover:text-white hover:bg-white/10" data-testid="search-toggle">
                  <Search className="h-5 w-5" />
                </Button>
              )}

              {/* Wishlist */}
              <Link to="/wishlist" data-testid="wishlist-link" className="hidden sm:block">
                <Button variant="ghost" size="icon" className="h-9 w-9 text-gray-300 hover:text-white hover:bg-white/10">
                  <Heart className="h-5 w-5" />
                </Button>
              </Link>

              {/* Cart */}
              <Link to="/cart" data-testid="cart-link">
                <Button variant="ghost" size="icon" className="h-9 w-9 text-gray-300 hover:text-white hover:bg-white/10 relative">
                  <ShoppingCart className="h-5 w-5" />
                  {cart.item_count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#EA580C] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                      {cart.item_count}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Login / Account */}
              {user ? (
                <div className="hidden sm:flex items-center gap-2">
                  {user.is_admin && (
                    <Link to="/admin" data-testid="admin-link">
                      <Button variant="ghost" size="sm" className="h-9 text-gray-300 hover:text-white hover:bg-white/10">
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button variant="ghost" size="icon" onClick={handleLogout} className="h-9 w-9 text-gray-300 hover:text-white hover:bg-white/10" data-testid="logout-btn">
                    <LogOut className="h-5 w-5" />
                  </Button>
                </div>
              ) : (
                <Link to="/login" data-testid="login-link" className="hidden sm:block">
                  <span className="text-sm text-gray-300 hover:text-white transition-colors">
                    Log in
                  </span>
                </Link>
              )}

              {/* CTA Button */}
              <Link to="/shop" className="hidden lg:block">
                <Button className="h-10 px-5 bg-white text-black hover:bg-gray-100 rounded-full font-medium">
                  Shop Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Announcement Bar */}
      <div className="bg-[#EA580C] text-white py-2 text-center text-sm px-4">
        <p>🚚 Free NZ Shipping on orders over $100</p>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0a0a0a] border-t border-white/10">
          <div className="px-4 py-4">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
            </form>

            <nav className="space-y-1">
              <Link to="/shop" className="block py-3 px-4 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 font-medium" onClick={() => setMobileMenuOpen(false)}>
                Shop All
              </Link>
              {categories.map((cat) => (
                <Link key={cat.id} to={`/shop?category=${encodeURIComponent(cat.name)}`} className="block py-3 px-4 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {cat.name}
                </Link>
              ))}
              <div className="border-t border-white/10 pt-2 mt-2">
                <Link to="/wishlist" className="block py-3 px-4 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Wishlist
                </Link>
                {user ? (
                  <>
                    {user.is_admin && (
                      <Link to="/admin" className="block py-3 px-4 rounded-lg text-[#EA580C] hover:bg-white/5 font-medium" onClick={() => setMobileMenuOpen(false)}>
                        Admin Panel
                      </Link>
                    )}
                    <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full text-left py-3 px-4 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 font-medium">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/login" className="block py-3 px-4 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Login / Register
                  </Link>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
